import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { apiService } from '../../services/api';
import { Box, Typography, Table, TableBody, TableCell, TableRow, CircularProgress, Paper } from '@mui/material';
import './ChecklistPrintView.css';

const ChecklistPrintView = () => {
  const { checklistId } = useParams();
  const [checklist, setChecklist] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchChecklist = async () => {
      try {
        const data = await apiService.getChecklistById(checklistId);
        setChecklist(data);
        setLoading(false);
        
        // Auto-print after a short delay to allow content to render
        setTimeout(() => {
          window.print();
        }, 500);
      } catch (err) {
        console.error('Error fetching checklist:', err);
        setError('Failed to load checklist');
        setLoading(false);
      }
    };

    if (checklistId) {
      fetchChecklist();
    }
  }, [checklistId]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !checklist) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">{error || 'Checklist not found'}</Typography>
      </Box>
    );
  }

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' });
  };

  const formatBoolean = (value) => {
    if (value === true || value === 'true') return '✓ Yes';
    if (value === false || value === 'false') return '✗ No';
    return 'N/A';
  };

  return (
    <Box className="print-view" sx={{ p: 4, maxWidth: '8.5in', margin: '0 auto', backgroundColor: 'white' }}>
      <Paper elevation={0} sx={{ p: 3 }}>
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 4, borderBottom: '2px solid #000', pb: 2 }}>
          <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
            VEHICLE MAINTENANCE INSPECTION CHECKLIST
          </Typography>
          <Typography variant="h6">
            Monthly Vehicle Inspection Report
          </Typography>
        </Box>

        {/* Basic Information */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2, borderBottom: '1px solid #666' }}>
            VEHICLE & DRIVER INFORMATION
          </Typography>
          <Table size="small">
            <TableBody>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', width: '30%' }}>Vehicle Number:</TableCell>
                <TableCell>{checklist.vehicle_number || 'N/A'}</TableCell>
                <TableCell sx={{ fontWeight: 'bold', width: '30%' }}>License Plate:</TableCell>
                <TableCell>{checklist.license_plate_number || 'N/A'}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Driver Name:</TableCell>
                <TableCell>{checklist.driver || 'N/A'}</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Inspection Date:</TableCell>
                <TableCell>{formatDate(checklist.date)}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Current Odometer:</TableCell>
                <TableCell>{checklist.current_odometer || 'N/A'}</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Previous Odometer:</TableCell>
                <TableCell>{checklist.previous_odometer || 'N/A'}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Miles Traveled:</TableCell>
                <TableCell colSpan={3}>{checklist.miles_traveled || 'N/A'}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Box>

        {/* Fluids & Maintenance */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2, borderBottom: '1px solid #666' }}>
            FLUIDS & ROUTINE MAINTENANCE
          </Typography>
          <Table size="small">
            <TableBody>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', width: '30%' }}>Oil Level:</TableCell>
                <TableCell width="20%">{formatBoolean(checklist.oil_level)}</TableCell>
                <TableCell sx={{ fontWeight: 'bold', width: '30%' }}>Next Oil Change:</TableCell>
                <TableCell>{formatDate(checklist.oil_change_next_date)}</TableCell>
              </TableRow>
              {checklist.oil_level_notes && (
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>Oil Level Notes:</TableCell>
                  <TableCell colSpan={3}>{checklist.oil_level_notes}</TableCell>
                </TableRow>
              )}
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Antifreeze Level:</TableCell>
                <TableCell>{formatBoolean(checklist.antifreeze_level)}</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Windshield Fluid:</TableCell>
                <TableCell>{formatBoolean(checklist.windshield_fluid)}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Transmission Fluid:</TableCell>
                <TableCell>{formatBoolean(checklist.transmission_fluid)}</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Brake Fluid:</TableCell>
                <TableCell>{formatBoolean(checklist.brake_fluid)}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Box>

        {/* Tire Information */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2, borderBottom: '1px solid #666' }}>
            TIRE INSPECTION
          </Typography>
          <Table size="small">
            <TableBody>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', width: '30%' }}>Left Front Pressure:</TableCell>
                <TableCell width="20%">{checklist.tire_pressure_lf || 'N/A'} PSI</TableCell>
                <TableCell sx={{ fontWeight: 'bold', width: '30%' }}>Right Front Pressure:</TableCell>
                <TableCell>{checklist.tire_pressure_rf || 'N/A'} PSI</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Left Rear Pressure:</TableCell>
                <TableCell>{checklist.tire_pressure_lr || 'N/A'} PSI</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Right Rear Pressure:</TableCell>
                <TableCell>{checklist.tire_pressure_rr || 'N/A'} PSI</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Tire Tread:</TableCell>
                <TableCell>{formatBoolean(checklist.tire_tread)}</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Last Tire Rotation:</TableCell>
                <TableCell>{formatDate(checklist.tire_rotation_date)}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Box>

        {/* Lights & Electrical */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2, borderBottom: '1px solid #666' }}>
            LIGHTS & ELECTRICAL SYSTEMS
          </Typography>
          <Table size="small">
            <TableBody>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', width: '30%' }}>Headlights:</TableCell>
                <TableCell width="20%">{formatBoolean(checklist.headlights)}</TableCell>
                <TableCell sx={{ fontWeight: 'bold', width: '30%' }}>Taillights:</TableCell>
                <TableCell>{formatBoolean(checklist.taillights)}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Brake Lights:</TableCell>
                <TableCell>{formatBoolean(checklist.brake_lights)}</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Running Lights:</TableCell>
                <TableCell>{formatBoolean(checklist.running_lights)}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Turn Signals:</TableCell>
                <TableCell>{formatBoolean(checklist.turn_signals)}</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Emergency Flashers:</TableCell>
                <TableCell>{formatBoolean(checklist.emergency_flashers)}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Box>

        {/* Safety Equipment */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2, borderBottom: '1px solid #666' }}>
            SAFETY EQUIPMENT
          </Typography>
          <Table size="small">
            <TableBody>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', width: '30%' }}>Fire Extinguisher:</TableCell>
                <TableCell width="20%">{formatBoolean(checklist.fire_extinguisher)}</TableCell>
                <TableCell sx={{ fontWeight: 'bold', width: '30%' }}>Charged:</TableCell>
                <TableCell>{formatBoolean(checklist.fire_extinguisher_charged)}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Extinguisher Cert Date:</TableCell>
                <TableCell colSpan={3}>{formatDate(checklist.fire_extinguisher_cert_date)}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>First Aid Kit:</TableCell>
                <TableCell>{formatBoolean(checklist.safety_kit_first_aid)}</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Eye Wash:</TableCell>
                <TableCell>{formatBoolean(checklist.safety_kit_eye_wash)}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Safety Flags:</TableCell>
                <TableCell>{formatBoolean(checklist.safety_kit_flags)}</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Flares:</TableCell>
                <TableCell>{formatBoolean(checklist.safety_kit_flares)}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Safety Cones:</TableCell>
                <TableCell>{formatBoolean(checklist.safety_kit_cones)}</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Hard Hat:</TableCell>
                <TableCell>{formatBoolean(checklist.safety_kit_hard_hat)}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Safety Goggles:</TableCell>
                <TableCell>{formatBoolean(checklist.safety_kit_goggles)}</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Gloves:</TableCell>
                <TableCell>{formatBoolean(checklist.safety_kit_gloves)}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Caution Tape:</TableCell>
                <TableCell>{formatBoolean(checklist.safety_kit_caution_tape)}</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Safety Harness:</TableCell>
                <TableCell>{formatBoolean(checklist.safety_kit_harness)}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Lumbar Support:</TableCell>
                <TableCell colSpan={3}>{formatBoolean(checklist.safety_kit_lumbar_support)}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Box>

        {/* Vehicle Condition */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2, borderBottom: '1px solid #666' }}>
            VEHICLE CONDITION
          </Typography>
          <Table size="small">
            <TableBody>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', width: '30%' }}>Overall Appearance:</TableCell>
                <TableCell width="20%">{formatBoolean(checklist.appearance)}</TableCell>
                <TableCell sx={{ fontWeight: 'bold', width: '30%' }}>Exterior Condition:</TableCell>
                <TableCell>{formatBoolean(checklist.exterior_condition)}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Interior Vacuumed:</TableCell>
                <TableCell>{formatBoolean(checklist.interior_vacuumed)}</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Interior Condition:</TableCell>
                <TableCell>{formatBoolean(checklist.interior_condition)}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Box>

        {/* Additional Equipment */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2, borderBottom: '1px solid #666' }}>
            ADDITIONAL EQUIPMENT
          </Typography>
          <Table size="small">
            <TableBody>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', width: '30%' }}>Sanitizing Supplies:</TableCell>
                <TableCell width="20%">{formatBoolean(checklist.sanitizing_supplies)}</TableCell>
                <TableCell sx={{ fontWeight: 'bold', width: '30%' }}>Extension Cords:</TableCell>
                <TableCell>{formatBoolean(checklist.extension_cords)}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Tire Traction Devices:</TableCell>
                <TableCell colSpan={3}>{formatBoolean(checklist.tire_traction_devices)}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Box>

        {/* Registration & Insurance */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2, borderBottom: '1px solid #666' }}>
            REGISTRATION & INSURANCE
          </Typography>
          <Table size="small">
            <TableBody>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', width: '30%' }}>Registration Current:</TableCell>
                <TableCell width="20%">{formatBoolean(checklist.registration)}</TableCell>
                <TableCell sx={{ fontWeight: 'bold', width: '30%' }}>Registration Expiry:</TableCell>
                <TableCell>{formatDate(checklist.registration_expiry)}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Insurance Current:</TableCell>
                <TableCell colSpan={3}>{formatBoolean(checklist.insurance)}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Box>

        {/* Notes */}
        {checklist.notes && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2, borderBottom: '1px solid #666' }}>
              ADDITIONAL NOTES
            </Typography>
            <Box sx={{ p: 2, border: '1px solid #ddd', borderRadius: 1, minHeight: '60px' }}>
              <Typography sx={{ whiteSpace: 'pre-wrap' }}>{checklist.notes}</Typography>
            </Box>
          </Box>
        )}

        {/* Signature */}
        <Box sx={{ mt: 4, pt: 3, borderTop: '2px solid #000' }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 3 }}>
            TECHNICIAN CERTIFICATION
          </Typography>
          <Table size="small">
            <TableBody>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', width: '30%' }}>Technician Signature:</TableCell>
                <TableCell>{checklist.tech_signature || 'N/A'}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Date:</TableCell>
                <TableCell>{formatDate(checklist.date)}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
          <Box sx={{ mt: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
            <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
              I certify that I have conducted this vehicle inspection and that all items checked are in good working condition 
              or have been noted for repair. Any deficiencies found have been reported to management.
            </Typography>
          </Box>
        </Box>

        {/* Footer */}
        <Box sx={{ mt: 4, pt: 2, borderTop: '1px solid #ddd', textAlign: 'center' }}>
          <Typography variant="caption" color="text.secondary">
            Mascott Vehicle Maintenance Compliance System
          </Typography>
          <Typography variant="caption" display="block" color="text.secondary">
            Printed: {new Date().toLocaleDateString()} {new Date().toLocaleTimeString()}
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
};

export default ChecklistPrintView;

